import {TIME} from '@stubhub/general-utils';
import {publish} from '@stubhub/pubsub-utils';
import {Controller} from '@stubhub/react-store-provider';
import {get} from '@stubhub/rest-method';
import {getSellSearchSuggestEventUrl} from '@stubhub/sell-url-helper';
import {formatURLDomain} from '@stubhub/sh-url-utils';

import {APP, SEARCH_TERM} from './constants';
import {
  deviceTypeSelector,
  hideCbtEventsSelector,
  isRedirectModalEnabledSelector,
  pointSelector,
  searchSuggestionsSelector,
  ukcmaSelector,
  viewportStateSelector,
} from './selectors';

const SEARCH_CACHE_MAX_AGE = parseInt(process.env.REACT_APP_CACHE_TTL_SEARCH, 10) || TIME.SECONDS_OF_MIN * 5;
const SUGGEST_API_END_POINT = '/search/suggest/v3';

const controller = new Controller({
  namespace: 'searchbar',

  mapStateToProps(state) {
    const viewportState = viewportStateSelector(state);
    const localState = this.getLocalState(state);
    const {term = ''} = localState;

    const {query = ''} = localState;
    const {response = ''} = localState;
    const {error = ''} = localState;

    // Either search suggestions based on the term
    const searchSuggestions = term ? searchSuggestionsSelector(localState) : [];

    return {
      hideCbtEvents: hideCbtEventsSelector(state),
      ukcma: ukcmaSelector(state),
      isRedirectModalEnabled: isRedirectModalEnabledSelector(state),
      point: pointSelector(state),
      screenSize: viewportState.screenSize,
      deviceType: deviceTypeSelector(state),
      loading: localState.loading,
      searchSuggestions,
      term,
      query,
      response,
      error,
      selectedSuggestionIndex: localState.selectedSuggestionIndex >= 0 ? localState.selectedSuggestionIndex : -1,
      isModalSearchActive: localState.isModalSearchActive || false,
    };
  },
  actionCreators: {
    onSearchItemClick,
    getSearchSuggestions,
    reset,
    updateSelectedSuggestionIndex,
    resetSearchComponent,
  },
  reducers: ['SEARCH_BAR_LOADING', 'SEARCH_BAR_LOADED', 'SEARCH_BAR_STATE_RESET', 'SEARCH_BAR_MODAL_SHOW'],
});

/**
 * Method to Make the Search Suggestion API Call and Process the Response according to Suggestion Variant
 * @param term [string] This string contains the searchTerm to be sanitized.
 * @return sanitizedTerm [string] The sanitized search string.
 *
 */
function sanitizeTerm(term) {
  let sanitizedTerm = term;
  if (sanitizedTerm) {
    sanitizedTerm = sanitizedTerm.trim();
  }

  return sanitizedTerm;
}

/**
 * Method to handle the Search Event
 * @param searchSuggestion [object] The props object passed by the component.
 * @param searchSuggestion.term [string] The Search Term to be used for triggering the search on the search result page.
 * @param searchSuggestion.entity [object] The Search
 *
 */
function onSearchItemClick({searchSuggestion}, browserHistory, app) {
  return (dispatch, getState) => {
    const gsConfig = getState().gsConfig || {};

    if (searchSuggestion) {
      const {searchTerm, webURI} = searchSuggestion;
      let href;
      if (app === APP.SELL) {
        if (searchTerm) {
          const formatSearchTerm = encodeURIComponent(sanitizeTerm(searchTerm));
          href = `sell/performerSearch?q=${formatSearchTerm}`;
        } else if (webURI) {
          href = getSellSearchSuggestEventUrl(webURI);
        }
      } else if (searchTerm) {
        href = `/find/s/?q=${encodeURIComponent(sanitizeTerm(searchTerm))}`;
      } else if (webURI) {
        href = webURI.indexOf('/') === 0 ? webURI : `/${webURI}`;
      }

      /**
       * If Valid URL is present format it and navigate to that URL
       *
       */
      if (href) {
        browserHistory.push(
          formatURLDomain({href, currentLocale: gsConfig.currentLocale, defaultLocale: gsConfig.defaultLocale})
        );
      }
    }

    /**
     * Reset Search Component After Clicking on any suggestion.
     *
     */
    dispatch({type: 'SEARCH_BAR_RESET'});
  };
}

/**
 * Method to update the selected search suggestion index.
 * @param selectedSuggestionIndex [number] The number denoting the index of the selected search suggestion.
 *
 */
function updateSelectedSuggestionIndex(selectedSuggestionIndex) {
  return (dispatch) => {
    dispatch({type: 'SEARCH_BAR_STATE_RESET', selectedSuggestionIndex});
  };
}

/**
 * Method to reset the Search Suggestion in the `localstate` of the search component.
 *
 */
function resetSearchComponent(fields = {}) {
  return (dispatch) => {
    dispatch({type: 'SEARCH_BAR_STATE_RESET', ...fields});
  };
}

/**
 * Method to dispatch a call to reset reducer
 *
 */
function reset() {
  return (dispatch) => {
    dispatch({type: 'SEARCH_BAR_RESET'});
  };
}

/**
 * Method to Process the Suggest API Response according to Suggestion Variant.
 * @param body [object] The object containing the response for the search suggestions api.
 * @param searchTerm [string] The current search string used to fetch the search suggestions.
 * @returns searchSuggestions [object] The Processed Search suggestion object whihc can used by the list component.
 *
 */
function getSuggestionsForVariant({body = {}, searchTerm}) {
  const searchSuggestions = processVariant7Response({body, searchTerm});

  return searchSuggestions;
}

/**
 * Method to get the Query Parameters for the Search Suggestion API Call.
 * @param opts [object] The object containing the values to override the default search spec for Search Suggestion API Call.
 * @returns searchSpec [object] The object containing the search spec for Search Suggestion API Call.
 *
 */
function getSearchQuery(opts) {
  let {term} = opts;
  const {
    sort = 'popularity desc',
    perfRows = 5,
    entityList = 'performer',
    minAvailableTickets = 0,
    includeNoneventEntities = true,
    shstore = 1,
    point,
    radius = 200,
    lang = true,
    geoId,
    geoExpansion,
  } = opts;

  /**
   * Trim Extra Spaces from Search Term Before API Call
   *
   */
  term = sanitizeTerm(term);

  /**
   * Default Search Specs
   *
   */
  const searchSpec = {
    term,
    sort,
    perfRows,
    entityList,
    minAvailableTickets,
    includeNoneventEntities,
    shstore,
    point,
    radius,
    lang,
    geoId,
    geoExpansion,
  };

  /**
   * Defaulting to variant that won for Search Suggestions Improvements test
   * TODO - Handle Using Switch Case when number of variants are increased.
   *
   */
  searchSpec.timeZoneOffsetInMinutes = new Date().getTimezoneOffset();
  searchSpec.entityList = 'event,performer,venue';
  searchSpec.withEventCount = true;
  searchSpec.enablePopular = false;
  searchSpec.sort = 'popularity desc, eventDateLocal asc,';

  return searchSpec;
}

/**
 * Method to Make the Search Suggestion API Call and Process the Response according to Suggestion Variant
 * @param opts [object] This object contains the options used to make the search suggestions API call.
 * @param opts.term [string] This string contains the term to be used for retrieving the search suggestions
 * @param opts.searchSuggestions [object] The Previous Search Suggestion Object used to re render the footer.
 *
 */
function getSearchSuggestions(opts = {}) {
  const {term, logger} = opts;
  let {searchSuggestions} = opts;

  return (dispatch, getState) => {
    /**
     * When Loading Empty Suggestion object update the footer with the latest search term
     *
     */
    if (searchSuggestions && searchSuggestions.length === 0) {
      searchSuggestions = getSuggestionsForVariant({searchTerm: term});
    }

    /**
     * Set the Component State to Loading and Show spinner
     *
     */
    dispatch({type: 'SEARCH_BAR_LOADING', searchSuggestions, term, loading: true, selectedSuggestionIndex: -1});

    /**
     * Getting the Application TLD/SH Store Id from `gsConfig`
     *
     */
    const {gsConfig, lang} = getState() || {};
    if (gsConfig) {
      opts.shstore = gsConfig.shstoreId;
    }

    /**
     * Make the Call to Search Suggestion API Endpoint
     *
     */
    const searchQuery = getSearchQuery(opts);

    return get({
      host: process.env.REACT_APP_API_HOST,
      path: SUGGEST_API_END_POINT,
      query: searchQuery,
      headers: {
        'Accept-Language': lang,
        Authorization: process.env.REACT_APP_API_SECRET,
      },
      json: true,
      logger,
      cache: true,
      cacheMaxAge: SEARCH_CACHE_MAX_AGE,
    })
      .then((body) => {
        const {searchbar = {}} = getState() || {};
        const {loading, term} = searchbar;
        const {searchTerm} = body;

        /**
         * Process the Response only if SearchTerm in the response matches the current SearchTerm in the component and the Current state of the Component is loading
         *
         */
        if (searchTerm === sanitizeTerm(term) && loading) {
          const searchSuggestions = getSuggestionsForVariant({body, searchTerm});
          dispatch({
            type: 'SEARCH_BAR_LOADED',
            searchSuggestions,
            term: searchTerm,
            query: searchQuery,
            response: body,
            error: '',
            loading: false,
            selectedSuggestionIndex: -1,
          });
          publish(SEARCH_TERM);
        }

        return '';
      })
      .catch((error) => {
        dispatch({
          type: 'SEARCH_BAR_LOADED',
          error,
          query: searchQuery,
        });
      });
  };
}

/**
 * Method to update the selected search suggestion index.
 * @returns suggestionFooter [object] The Suggestion Item object containing the footer object.
 *
 */
function getSearchSuggestionFooterItem({searchTerm}) {
  return {
    type: 'footer',
    searchTerm,
  };
}

/**
 * Method to get the performer Suggestion Item for the search performer suggestion item.
 * @param performerSuggestionObject [object] The Search Performer Suggestion Object.
 * @returns performerSuggestionItem [object] The Suggestion Item object containing the Performer Suggestion item.
 *
 */
function getPerformerSuggestionItem(performerSuggestionObject = {}) {
  const performerSuggestionItem = {...performerSuggestionObject};

  let categoryId;
  let genreId = '';
  let groupingId;
  if (performerSuggestionItem.ancestors) {
    if (performerSuggestionItem.ancestors.groupings && performerSuggestionItem.ancestors.groupings.length > 0) {
      groupingId =
        performerSuggestionItem.ancestors.groupings[performerSuggestionItem.ancestors.groupings.length - 1].id;

      if (genreId === '') {
        genreId = groupingId;
      }
    }
    if (performerSuggestionItem.ancestors.categories && performerSuggestionItem.ancestors.categories.length > 0) {
      categoryId =
        performerSuggestionItem.ancestors.categories[performerSuggestionItem.ancestors.categories.length - 1].id;
      if (genreId === '') {
        genreId = categoryId;
      }
    }
  }

  performerSuggestionItem.actualType = performerSuggestionItem.type;
  performerSuggestionItem.type = 'performer_item';

  return performerSuggestionItem;
}

/**
 * Method to process the venues Suggestions in the Search Suggestion API response.
 * @param maxVenueSuggestions [number=2] The max number of venue suggestions to be added to the SuggestionObject.
 * @param count [number] The counter having the value of the count for the search suggestion.
 * @returns searchSuggestions [Object] The Processed Venues Suggestions Object.
 * @returns count [number] The Last count index value in the function.
 *
 */
function processVenueSuggestions({venues = {}, maxVenueSuggestions = 2, count = 0}) {
  const searchSuggestions = [];

  /**
   * Build the Suggestion object for the Events Sections from `queryLogSuggestions`.
   *
   */
  if (venues && venues.length > 0) {
    /**
     * Event Header Item
     *
     */
    searchSuggestions.push(getHeaderSuggestionItem('VENUES'));

    /**
     * Show MAX of 2 events (configured by the variable `maxEventSuggestions`)
     * NOTE - Extract into function if more variants are added to show events in the future.
     *
     */
    for (let i = 0, venuesArrLen = Math.min(maxVenueSuggestions, venues.length); i < venuesArrLen; i++) {
      let venueItem = {...venues[i]};
      venueItem.type = 'venue_item';

      /**
       * Events Partner Config
       *
       */
      venueItem = {...venueItem};

      searchSuggestions.push({...venueItem});
      count++;
    }
  }

  return {
    searchSuggestions,
    count,
  };
}

/**
 * Method to get the performer Suggestion Item for the search performer suggestion item.
 * @param text [String] The placeholder text key for Internationalization.
 * @param useIcon [Boolean] Whether this header entry should use an icon. The icon file name is by convention.
 * @returns headerSuggestionItem [object] The Header Suggestion item.
 *
 */
function getHeaderSuggestionItem(text, useIcon = true) {
  const headerSuggestionItem = {
    type: 'header',
    text,
    notSelectable: true,
    notClickable: true,
    icon: useIcon ? `icon-${text}.svg` : '',
  };

  return headerSuggestionItem;
}

function processImprovedEventSuggestions({events = {}, maxEventSuggestions = 3, searchTerm = '', count = 0}) {
  const searchSuggestions = [];

  /**
   * Build the Suggestion object for the Events Sections from `queryLogSuggestions`.
   *
   */
  if (events && events.length > 0) {
    /**
     * Event Header Item
     *
     */
    searchSuggestions.push(getHeaderSuggestionItem('EVENTS'));

    for (let eventsArrLen = Math.min(maxEventSuggestions, events.length), i = 0; i < eventsArrLen; i++) {
      let genreId = '';
      let categoryId;
      const secondaryPerformerIds = [];
      let groupingId;
      genreId = events[i].performers ? events[i].performers[0].id : '';

      if (events[i].ancestors) {
        if (events[i].ancestors.groupings && events[i].ancestors.groupings.length > 0) {
          groupingId = events[i].ancestors.groupings[events[i].ancestors.groupings.length - 1].id;
          if (genreId === '') {
            genreId = groupingId;
          }
        }
        if (events[i].ancestors.categories && events[i].ancestors.categories.length > 0) {
          categoryId = events[i].ancestors.categories[events[i].ancestors.categories.length - 1].id;
          if (genreId === '') {
            genreId = categoryId;
          }
        }
      }
      if (events[i].performers) {
        for (let j = 1; j < events[i].performers.length; j++) {
          secondaryPerformerIds.push(events[i].performers[j].id);
        }
      }

      const eventItem = {...events[i]};
      eventItem.type = 'event_item';

      searchSuggestions.push({...eventItem});
      count++;
    }

    const moreEventsitem = {
      type: 'link_item',
      name: 'more events',
      notSelectable: true,
      searchTerm,
    };
    searchSuggestions.push({...moreEventsitem});
  }

  return {
    searchSuggestions,
    count,
  };
}

/**
 * Method to process the Performer suggestions in the Search Suggestion API response.
 * @param maxPerformerSuggestions [number=2] The max number of event suggestions to be added to the SuggestionObject.
 * @param count [number] The counter having the value of the count for the search suggestion.
 * @returns searchSuggestions [Object] The Processed Events Suggestions Object.
 * @returns queryLogIndex [number] The Last queryLogIndex index value in the function.
 * @returns count [number] The Last count index value in the function.
 *
 */
function processImprovedPerformerSuggestions({performers = {}, maxPerformerSuggestions = 3, count = 1}) {
  const searchSuggestions = [];

  /**
   * Identified Primary Performer Item
   *
   */
  if (performers && performers.length > 0) {
    for (let i = 0, performerArrLen = Math.min(maxPerformerSuggestions, performers.length); i < performerArrLen; i++) {
      const performerItem = getPerformerSuggestionItem(performers[i]);
      searchSuggestions.push(performerItem);
      count++;
    }
  }

  return {
    searchSuggestions,
    count,
  };
}

/**
 * Method to process the Poplar Searches Suggestions in the Search Suggestion API response.
 * @param queryLogSuggestions [Object] The `queryLogSuggestions` section of the Search Suggestion API response.
 * @param queryLogIndex [number] The global index used for current item.
 * @param maxPopularSearchesSuggestions [number=2] The max number of event suggestions to be added to the SuggestionObject.
 * @param count [number] The counter having the value of the count for the search suggestion.
 * @returns searchSuggestions [Object] The Processed Events Suggestions Object.
 * @returns queryLogIndex [number] The Last queryLogIndex index value in the function.
 * @returns count [number] The Last count index value in the function.
 *
 */
function processPopularSearchSuggestions({
  queryLogSuggestions = {},
  queryLogIndex = 0,
  maxPopularSearchesSuggestions = 2,
  count = 0,
}) {
  const searchSuggestions = [];
  const queryLogSuggestionsObj = queryLogSuggestions.queryLogSuggestions;

  if (queryLogSuggestionsObj && queryLogSuggestionsObj.length > 0 && maxPopularSearchesSuggestions > 0) {
    /**
     * Popular Searches Header Item
     *
     */
    searchSuggestions.push(getHeaderSuggestionItem('POPULAR_SEARCHES'));

    /**
     * Popular Searches Suggestions Item
     *
     */
    for (
      let i = 0, popularSearchesArrLen = Math.min(maxPopularSearchesSuggestions, queryLogSuggestionsObj.length);
      i < popularSearchesArrLen;
      i++
    ) {
      const popularSearchesSuggestionObject = queryLogSuggestionsObj[i];
      popularSearchesSuggestionObject.searchTerm = popularSearchesSuggestionObject.name;
      const popularSearchItem = getPerformerSuggestionItem(popularSearchesSuggestionObject);
      searchSuggestions.push(popularSearchItem);
      queryLogIndex++;
      count++;
    }
  }

  return {
    searchSuggestions,
    queryLogIndex,
    count,
  };
}

/**
 * Method to process the Search Suggestion API response to conform to Variant 7 specs.
 * @param body [Object] Search Suggestion API Response.
 * @param searchTerm [string] The current search string used to fetch the search suggestions.
 * @returns searchSuggestionObject [Array] The Processed Suggestion API Response object for Variant 7.
 *
 */
function processVariant7Response({body, searchTerm}) {
  const SEARCH_SUGGEST_ITEMS_LIMIT = 8;

  let searchSuggestionObject = [];
  let otherSuggestionObject = [];
  let venueSuggestionObject = [];
  const queryLogIndex = 0;
  const {queryLogSuggestions, events, venues, performers} = body;
  let count;

  /**
   * Check for the `queryLogSuggestions` block in the Response
   *
   */

  if (queryLogSuggestions || events || venues || performers) {
    if (queryLogSuggestions || events || performers) {
      /**
       * Build the Suggestion object for the Artist/Team Sections from `queryLogSuggestions`.
       *
       */
      const performerSuggestionsResponse = processImprovedPerformerSuggestions({
        performers,
      });
      const {count: performerCount} = performerSuggestionsResponse;
      count = performerCount;

      /**
       * Build the Suggestion object for the Events Sections from `queryLogSuggestions`.
       *
       */
      const eventSuggestionsResponse = processImprovedEventSuggestions({
        events,
        searchTerm,
        count,
      });

      /**
       * Build the Suggestion object for the Trending Searches Sections from `queryLogSuggestions`. Limit the number to max out to SEARCH_SUGGEST_ITEMS_LIMIT
       *
       */
      const popularSearchSuggestionsResponse = processPopularSearchSuggestions({
        queryLogIndex,
        queryLogSuggestions,
        searchTerm,
        maxPopularSearchesSuggestions: SEARCH_SUGGEST_ITEMS_LIMIT - queryLogIndex,
        count,
      });

      /**
       * Aggregate the Search Suggestions.
       *
       */
      otherSuggestionObject = [
        ...performerSuggestionsResponse.searchSuggestions,
        ...eventSuggestionsResponse.searchSuggestions,
        ...popularSearchSuggestionsResponse.searchSuggestions,
      ];
    }
    if (venues) {
      /**
       * Build the Suggestion object for the Events Sections from `queryLogSuggestions`.
       *
       */
      const venueSuggestionsResponse = processVenueSuggestions({
        venues,
      });
      venueSuggestionObject = [...venueSuggestionsResponse.searchSuggestions];
    }
  }

  searchSuggestionObject = [...otherSuggestionObject, ...venueSuggestionObject];

  /**
   * Append the Footer to be shown in Search Suggestion
   *
   */
  searchSuggestionObject.push(getSearchSuggestionFooterItem({searchTerm}));

  return searchSuggestionObject;
}

controller.addResetReducer(['SEARCH_BAR_RESET']);

export default controller;
